import { format, isWithinInterval, isBefore, isAfter } from "date-fns";

interface EventForSummary {
  id: string;
  staffId?: string;
  assignedTo?: string;
  assignedStaff?: { id: string }[];
  startTime: Date;
  endTime: Date;
  highestConflictSeverity?: string;
}

interface BusyWindow {
  start: Date;
  end: Date;
  eventId: string;
}

interface StaffDaySummary {
  criticalConflicts: number;
  highConflicts: number;
  mediumConflicts: number;
  lowConflicts: number;
  busyNow: boolean;
  busyUntil: string | null;
  nextFreeSlot: Date | null;
  busyWindows: BusyWindow[];
  todaysEvents: EventForSummary[];
}

/**
 * Generates Outlook-style conflict and availability summary for a staff member on a specific day
 * 
 * @param staffId - The staff member's ID
 * @param events - All calendar events
 * @param day - The day to analyze
 * @returns Summary with conflict counts, busy status, and availability windows
 */
export function staffSummaryForDay(
  staffId: string,
  events: EventForSummary[],
  day: Date
): StaffDaySummary {
  const dayStr = format(day, "yyyy-MM-dd");
  const now = new Date();

  // Filter events for this staff member on this day
  const todaysEvents = events.filter((ev) => {
    // Check if staff is assigned (supports both single and multi-staff assignments)
    const isAssignedToStaff = 
      ev.assignedTo === staffId ||
      ev.staffId === staffId ||
      ev.assignedStaff?.some((staff) => staff.id === staffId);

    if (!isAssignedToStaff) return false;

    // Skip events without valid times
    if (!ev.startTime || !ev.endTime) return false;

    // Check if event occurs on this day
    const eventStartStr = format(new Date(ev.startTime), "yyyy-MM-dd");
    const eventEndStr = format(new Date(ev.endTime), "yyyy-MM-dd");

    return eventStartStr === dayStr || eventEndStr === dayStr;
  });

  // Count conflicts by severity
  const criticalConflicts = todaysEvents.filter(
    (ev) => ev.highestConflictSeverity?.toLowerCase() === "critical"
  ).length;

  const highConflicts = todaysEvents.filter(
    (ev) => ev.highestConflictSeverity?.toLowerCase() === "high"
  ).length;

  const mediumConflicts = todaysEvents.filter(
    (ev) => ev.highestConflictSeverity?.toLowerCase() === "medium"
  ).length;

  const lowConflicts = todaysEvents.filter(
    (ev) => ev.highestConflictSeverity?.toLowerCase() === "low"
  ).length;

  // Determine if staff is currently busy
  const currentEvents = todaysEvents.filter((ev) => {
    const start = new Date(ev.startTime);
    const end = new Date(ev.endTime);
    return isWithinInterval(now, { start, end });
  });

  const busyNow = currentEvents.length > 0;

  // Find when current busy period ends
  let busyUntil: string | null = null;
  if (busyNow && currentEvents.length > 0) {
    // Find the latest end time among current events
    const latestEnd = currentEvents.reduce((latest, ev) => {
      const end = new Date(ev.endTime);
      return end > latest ? end : latest;
    }, new Date(currentEvents[0].endTime));

    busyUntil = format(latestEnd, "h:mm a");
  }

  // Build busy windows (sorted by start time)
  const busyWindows: BusyWindow[] = todaysEvents
    .map((ev) => ({
      start: new Date(ev.startTime),
      end: new Date(ev.endTime),
      eventId: ev.id,
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  // Find next free slot (next time after now when staff isn't busy)
  let nextFreeSlot: Date | null = null;
  if (busyNow && busyWindows.length > 0) {
    // Find the gap after the current busy period
    const currentBusyEnd = busyWindows.find((window) => 
      isWithinInterval(now, { start: window.start, end: window.end })
    )?.end;

    if (currentBusyEnd) {
      // Check if there's another event after this one
      const nextEvent = busyWindows.find((window) => 
        isAfter(window.start, currentBusyEnd)
      );

      // If there's a gap, the next free slot is right after current busy period
      // If next event starts immediately, the free slot is after that event
      if (!nextEvent || isAfter(nextEvent.start, currentBusyEnd)) {
        nextFreeSlot = currentBusyEnd;
      }
    }
  } else if (!busyNow && busyWindows.length > 0) {
    // Not currently busy - find next busy period
    const nextBusyWindow = busyWindows.find((window) => 
      isAfter(window.start, now)
    );

    // Free slot is now (until next busy window starts)
    nextFreeSlot = now;
  } else {
    // No events today - completely free
    nextFreeSlot = now;
  }

  return {
    criticalConflicts,
    highConflicts,
    mediumConflicts,
    lowConflicts,
    busyNow,
    busyUntil,
    nextFreeSlot,
    busyWindows,
    todaysEvents,
  };
}

/**
 * Get a human-readable summary string for staff availability
 */
export function getAvailabilitySummary(summary: StaffDaySummary): string {
  if (summary.busyNow) {
    return `Busy until ${summary.busyUntil}`;
  }

  if (summary.todaysEvents.length === 0) {
    return "Available all day";
  }

  if (summary.nextFreeSlot) {
    return `Free now`;
  }

  return "Available";
}

/**
 * Get a conflict badge description
 */
export function getConflictBadge(summary: StaffDaySummary): string | null {
  if (summary.criticalConflicts > 0) {
    return `${summary.criticalConflicts} critical conflict${summary.criticalConflicts > 1 ? 's' : ''}`;
  }

  if (summary.highConflicts > 0) {
    return `${summary.highConflicts} conflict${summary.highConflicts > 1 ? 's' : ''}`;
  }

  return null;
}
